// src/api/client.ts

const API_BASE = 'https://mercuritan.rejola.com/api/public';
//const API_BASE = 'http://mercurianequity-local.com:8080/api/public';
const TOKEN_KEY = 'me_auth_token';
const USER_KEY = 'me_auth_user';
const SESSION_STARTED_KEY = 'me_auth_session_started_at';
const SESSION_DURATION_MS_DEFAULT = 60 * 60 * 1000; // 1 hour
const SESSION_DURATION_MS_REMEMBER = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface AuthUser {
  id?: number;
  full_name?: string | null;
  email?: string;
  phone?: string | null;
  role?: string;
  joined_at?: string | null;
  created_at?: string | null;
  subscription_started_at?: string | null;
  subscription_expires_at?: string | null;
}

export interface ArticleSummary {
  id: number;
  title: string;
  slug?: string;
  excerpt?: string;
  content_type: 'text' | 'pdf' | string;
  pdf_url?: string | null;
  published_at?: string;
}

export interface AdminArticleSummary extends ArticleSummary {
  pdf_filename?: string | null;
  published?: boolean | number;
  created_at?: string;
}

export interface AdminArticleDetail extends AdminArticleSummary {
  body?: string | null;
}

export interface AdminUserSummary {
  id: number;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  is_active?: boolean | number | null;
  joined_at?: string | null;
  created_at?: string | null;
  subscription_started_at?: string | null;
  subscription_expires_at?: string | null;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  description?: string | null;
  amount: number;
  currency?: string | null;
  billing_cycle: string;
  billing_start_type: string;
  billing_anchor_day?: number | null;
  duration_days: number;
  sort_order?: number | null;
  is_active?: boolean | number | null;
  billing_summary?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AdminPlanPayload {
  name: string;
  description?: string;
  amount: number;
  currency?: string;
  billing_cycle: string;
  duration_days: number;
  sort_order?: number;
  is_active?: boolean;
}

export interface MemberSubscriptionDetails {
  id: number;
  plan_id: number;
  plan_name: string;
  plan_description?: string | null;
  amount: number;
  currency: string;
  billing_cycle: string;
  duration_days: number;
  status: string;
  starts_at?: string | null;
  expires_at?: string | null;
  days_left?: number | null;
  is_active?: boolean;
  // New fields from API to indicate renewal behaviour
  renewal_status?: string | null;
  will_renew?: boolean | null;
}

export interface MemberPaymentDetails {
  id: number;
  gateway?: string | null;
  payment_reference?: string | null;
  amount: number;
  currency: string;
  status?: string | null;
  paid_at?: string | null;
  sub_starts_at?: string | null;
  sub_expires_at?: string | null;
  sub_status?: string | null;
  stripe_subscription_id?: string | null;
  plan_name?: string | null;
  plan_description?: string | null;
  billing_cycle?: string | null;
  plan_duration_days?: number | null;
  plan_amount?: string | number | null;
  plan_currency?: string | null;
}

export interface CheckoutSessionResponse {
  success: boolean;
  session_id?: string;
  checkout_url?: string;
  message?: string;
  status?: number;
}

export interface MemberSubscriptionResponse {
  success: boolean;
  subscription: MemberSubscriptionDetails | null;
  latest_payment: MemberPaymentDetails | null;
  message?: string;
  status?: number;
}

export interface ApiErrorResponse {
  success?: boolean;
  status?: number;
  message?: string;
}

let authToken: string | null =
  typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;

function setSessionStartedAt(timestamp: number | null, rememberMe?: boolean) {
  if (typeof window === 'undefined') return;

  if (timestamp) {
    localStorage.setItem(SESSION_STARTED_KEY, String(timestamp));
    if (rememberMe) {
      localStorage.setItem('me_remember_me', '1');
    } else {
      localStorage.removeItem('me_remember_me');
    }
  } else {
    localStorage.removeItem(SESSION_STARTED_KEY);
    localStorage.removeItem('me_remember_me');
  }
}

function getSessionStartedAt() {
  if (typeof window === 'undefined') return null;

  const raw = localStorage.getItem(SESSION_STARTED_KEY);
  if (!raw) return null;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function hasValidPortalSession() {
  const sessionStartedAt = getSessionStartedAt();
  if (!sessionStartedAt) {
    return false;
  }
  const rememberMe = typeof window !== 'undefined' && localStorage.getItem('me_remember_me') === '1';
  const duration = rememberMe ? SESSION_DURATION_MS_REMEMBER : SESSION_DURATION_MS_DEFAULT;
  return Date.now() - sessionStartedAt < duration;
}

export function setAuthToken(token: string | null, rememberMe?: boolean) {
  authToken = token;
  if (typeof window !== 'undefined') {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      setSessionStartedAt(Date.now(), rememberMe);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      setSessionStartedAt(null);
    }
  }
}

export function setAuthUser(user: AuthUser | null) {
  if (typeof window === 'undefined') return;
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_KEY);
  }
}

export function getStoredAuth() {
  let token = authToken;
  if (typeof window !== 'undefined' && !token) {
    token = localStorage.getItem(TOKEN_KEY);
  }

  if (token && !hasValidPortalSession()) {
    clearAuth();
    return { token: null, user: null };
  }

  let user: AuthUser | null = null;
  if (typeof window !== 'undefined') {
    const raw = localStorage.getItem(USER_KEY);
    if (raw) {
      try {
        user = JSON.parse(raw);
      } catch {
        user = null;
      }
    }
  }
  return { token, user };
}

export function isAdminUser(user?: AuthUser | null) {
  return user?.role === 'admin';
}

export function hasActiveSubscription(user?: AuthUser | null) {
  if (!user?.subscription_expires_at) {
    return false;
  }

  const expiresAt = new Date(user.subscription_expires_at.replace(' ', 'T')).getTime();
  if (Number.isNaN(expiresAt)) {
    return false;
  }

  return expiresAt > Date.now();
}

export function getApiDateTimestamp(value?: string | null) {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value.replace(' ', 'T')).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

export function hasActiveMemberSubscription(subscription?: MemberSubscriptionDetails | null) {
  if (!subscription) {
    return false;
  }

  if (subscription.is_active === false) {
    return false;
  }

  // Allow access until the subscription's expiry timestamp even if the subscription
  // has been marked as 'cancelled' (i.e. cancelled at period end). The backend
  // should set `is_active` to false only when access is fully ended.
  const expiresAt = getApiDateTimestamp(subscription.expires_at);
  if (expiresAt === null) {
    return false;
  }

  return expiresAt > Date.now();
}

export function clearAuth() {
  setAuthToken(null);
  setAuthUser(null);
}

export function getPortalEntryPath() {
  const { token, user } = getStoredAuth();

  if (!token) {
    return '/member/login';
  }

  return isAdminUser(user) ? '/admin' : '/member/articles';
}

function authHeaders(): Record<string, string> {
  const { token } = getStoredAuth();

  if (token) {
    authToken = token;
    return { Authorization: `Bearer ${token}` };
  }

  return {};
}

function mergeStoredUser(nextUser?: AuthUser | null) {
  const { user } = getStoredAuth();

  if (!nextUser) {
    return user;
  }

  return {
    ...user,
    ...nextUser,
  };
}

function extractProfileUser(payload: any): AuthUser | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if (payload.user && typeof payload.user === 'object') {
    return payload.user;
  }

  if (payload.full_name || payload.email || payload.phone || payload.joined_at || payload.created_at) {
    return payload;
  }

  return null;
}

async function parseApiResponse(res: Response) {
  const responseText = await res.text();

  try {
    return responseText ? JSON.parse(responseText) : {};
  } catch {
    return responseText ? { message: responseText } : {};
  }
}

function extractAdminUsers(payload: any): AdminUserSummary[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.users)) {
    return payload.users;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
}

function extractPlans(payload: any): SubscriptionPlan[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.plans)) {
    return payload.plans;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
}

export async function apiRegister(fullName: string, email: string, password: string, phone?: string) {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ full_name: fullName, email, password, phone }),
  });
  const data = await res.json();
  if (data.token) setAuthToken(data.token);
  if (data.user) setAuthUser(data.user);
  return data;
}

export async function apiLogin(email: string, password: string, rememberMe?: boolean) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await parseApiResponse(res);

  if (data.token) setAuthToken(data.token, rememberMe);
  if (data.user) setAuthUser(data.user);

  return {
    ...data,
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    status: res.status,
    message: data?.message || data?.error || (res.ok ? '' : 'Unable to log in right now.'),
  };
}

export async function apiForgotPassword(email: string) {
  const res = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  const data = await parseApiResponse(res);

  return {
    ...data,
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    status: res.status,
    message: data?.message || (res.ok ? 'Check your email for the OTP.' : 'Unable to send the OTP right now.'),
  };
}

export async function apiResetPassword(email: string, otp: string, password: string) {
  const res = await fetch(`${API_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp, password }),
  });

  const data = await parseApiResponse(res);

  return {
    ...data,
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    status: res.status,
    message: data?.message || (res.ok ? 'Your password has been reset successfully.' : 'Unable to reset your password right now.'),
  };
}

export async function apiLogout() {
  clearAuth();
  const res = await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.json();
}

export async function apiGetMe() {
  const res = await fetch(`${API_BASE}/me`, {
    headers: authHeaders(),
  });

  const responseText = await res.text();
  let data: any = null;

  try {
    data = responseText ? JSON.parse(responseText) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message || 'Unable to load your profile right now.',
    };
  }

  const profileUser = extractProfileUser(data);

  if (profileUser) {
    setAuthUser(mergeStoredUser(profileUser));
  }

  return data;
}

export async function apiGetMySubscription(): Promise<MemberSubscriptionResponse> {
  const res = await fetch(`${API_BASE}/me/subscription`, {
    headers: authHeaders(),
  });

  const data = await parseApiResponse(res);

  return {
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    subscription: data?.subscription ?? null,
    latest_payment: data?.latest_payment ?? null,
    message: data?.message || (!res.ok ? 'Unable to load subscription details right now.' : ''),
    status: res.status,
  };
}

export async function apiCancelMySubscription(): Promise<{ success: boolean; message?: string; status?: number }> {
  const res = await fetch(`${API_BASE}/me/subscription/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });

  const data = await parseApiResponse(res);

  return {
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    message: data?.message || (res.ok ? '' : 'Unable to cancel subscription right now.'),
    status: res.status,
  };
}

export async function apiGetMyPayments(): Promise<MemberPaymentDetails[] | ApiErrorResponse> {
  const res = await fetch(`${API_BASE}/me/payments`, {
    headers: authHeaders(),
  });

  const data = await parseApiResponse(res);

  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message || 'Unable to load your payments right now.',
    };
  }

  return Array.isArray(data) ? data : data?.payments || [];
}

export async function apiGetMyPaymentById(id: number): Promise<MemberPaymentDetails | ApiErrorResponse> {
  const res = await fetch(`${API_BASE}/me/payments/${id}`, {
    headers: authHeaders(),
  });

  const data = await parseApiResponse(res);

  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message || 'Unable to load payment detail right now.',
    };
  }

  return data;
}

export async function apiAdminGetUserPayments(userId: number): Promise<MemberPaymentDetails[] | ApiErrorResponse> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/payments`, {
    headers: authHeaders(),
  });

  const data = await parseApiResponse(res);

  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message || 'Unable to load user payments right now.',
    };
  }

  return Array.isArray(data) ? data : data?.payments || [];
}

export async function apiAdminGetAllPayments(): Promise<MemberPaymentDetails[] | ApiErrorResponse> {
  const res = await fetch(`${API_BASE}/admin/payments`, {
    headers: authHeaders(),
  });

  const data = await parseApiResponse(res);

  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message || 'Unable to load payments right now.',
    };
  }

  return Array.isArray(data) ? data : data?.payments || [];
}

export async function apiUpdateMe({ full_name, phone, password }: { full_name?: string; phone?: string; password?: string }) {
  const res = await fetch(`${API_BASE}/me`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ full_name, phone, password }),
  });

  const data = await res.json();

  if (data?.success && data?.user) {
    setAuthUser(mergeStoredUser(data.user));
  }

  return data;
}

export async function apiGetPlans() {
  const res = await fetch(`${API_BASE}/plans`, {
    headers: authHeaders(),
  });

  const data = await parseApiResponse(res);

  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message || 'Unable to load plans right now.',
    };
  }

  return extractPlans(data);
}

export async function apiConfirmPayment({ planId, amount, currency, gateway, paymentReference }: {
  planId: number,
  amount: number,
  currency: string,
  gateway: string,
  paymentReference: string,
}) {
  const res = await fetch(`${API_BASE}/auth/payment-success`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      plan_id: planId,
      amount,
      currency,
      gateway,
      payment_reference: paymentReference,
    }),
  });
  return res.json();
}

export async function apiCreateCheckoutSession(planId: number): Promise<CheckoutSessionResponse> {
  const res = await fetch(`${API_BASE}/payments/checkout/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ plan_id: planId }),
  });

  const data = await parseApiResponse(res);

  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message || 'Unable to create checkout session right now.',
    };
  }

  return {
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    session_id: data?.session_id,
    checkout_url: data?.checkout_url,
    message: data?.message || '',
    status: res.status,
  };
}

export async function apiEnableComplimentaryAccess() {
  const plans = await apiGetPlans();
  const firstPlan = Array.isArray(plans) && plans.length > 0 ? plans[0] : null;

  const result = await apiConfirmPayment({
    planId: firstPlan?.id || 0,
    amount: firstPlan?.amount || 0,
    currency: firstPlan?.currency || 'INR',
    gateway: 'test',
    paymentReference: `complimentary-${Date.now()}`,
  });

  if (result?.success) {
    const { user } = getStoredAuth();
    setAuthUser({
      ...user,
      subscription_expires_at: result.subscription_expires_at || user?.subscription_expires_at || null,
    });
  }

  return result;
}

export async function apiListArticles(retryOnForbidden = true): Promise<ArticleSummary[] | ApiErrorResponse> {
  const headers = authHeaders();

  console.log('[apiListArticles] API base:', API_BASE);
  console.log('[apiListArticles] token:', authToken);
  console.log('[apiListArticles] headers:', headers);

  const res = await fetch(`${API_BASE}/articles`, {
    headers,
  });

  const responseText = await res.text();

  console.log('[apiListArticles] status:', res.status, res.statusText);
  console.log('[apiListArticles] response:', responseText);

  if (res.status === 403 && retryOnForbidden) {
    console.warn('[apiListArticles] Received 403. Trying temporary access activation and retrying once.');

    const accessResult = await apiEnableComplimentaryAccess();
    console.log('[apiListArticles] complimentary access result:', accessResult);

    if (accessResult?.success) {
      return apiListArticles(false);
    }
  }

  try {
    return responseText ? JSON.parse(responseText) : [];
  } catch {
    return {
      success: false,
      status: res.status,
      message: responseText || 'Unable to parse server response.',
    };
  }
}

export async function apiGetArticle(id: number) {
  const res = await fetch(`${API_BASE}/articles/${id}`, {
    headers: authHeaders(),
  });
  return res.json();
}

export async function apiAdminListArticles(status: 'all' | 'published' | 'unpublished' = 'all'): Promise<AdminArticleSummary[] | ApiErrorResponse> {
  const res = await fetch(`${API_BASE}/admin/articles?status=${status}`, {
    headers: authHeaders(),
  });

  const responseText = await res.text();

  try {
    return responseText ? JSON.parse(responseText) : [];
  } catch {
    return {
      success: false,
      status: res.status,
      message: responseText || 'Unable to parse server response.',
    };
  }
}

export async function apiAdminGetArticleById(id: number): Promise<AdminArticleDetail | ApiErrorResponse> {
  const listResponse = await apiAdminListArticles('all');

  if (!Array.isArray(listResponse)) {
    return listResponse;
  }

  const article = listResponse.find(item => item.id === id);

  if (!article) {
    return {
      success: false,
      status: 404,
      message: 'Article not found.',
    };
  }

  return article;
}

export async function apiAdminUploadPdf(file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API_BASE}/admin/uploads/pdf`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });

  return res.json();
}

export async function apiAdminCreateArticle(article: any) {
  const res = await fetch(`${API_BASE}/admin/articles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(article),
  });
  return res.json();
}

export async function apiAdminUpdateArticle(id: number, article: Record<string, unknown>) {
  const res = await fetch(`${API_BASE}/admin/articles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(article),
  });

  return res.json();
}

export async function apiAdminDeleteArticle(id: number) {
  const res = await fetch(`${API_BASE}/admin/articles/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  return res.json();
}

export async function apiAdminPublishArticle(id: number) {
  const res = await fetch(`${API_BASE}/admin/articles/${id}/publish`, {
    method: 'POST',
    headers: authHeaders(),
  });

  return res.json();
}

export async function apiAdminUnpublishArticle(id: number) {
  const res = await fetch(`${API_BASE}/admin/articles/${id}/unpublish`, {
    method: 'POST',
    headers: authHeaders(),
  });

  return res.json();
}

export async function apiAdminListUsers(): Promise<AdminUserSummary[] | ApiErrorResponse> {
  const res = await fetch(`${API_BASE}/admin/users`, {
    headers: authHeaders(),
  });

  const data = await parseApiResponse(res);

  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message || 'Unable to load subscribers right now.',
    };
  }

  return extractAdminUsers(data);
}

export async function apiAdminUpdateUserStatus(id: number, isActive: boolean) {
  const res = await fetch(`${API_BASE}/admin/users/${id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ is_active: isActive }),
  });

  const data = await parseApiResponse(res);

  return {
    ...data,
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    status: res.status,
    message: data?.message || (res.ok ? 'User status updated successfully.' : 'Unable to update user status right now.'),
  };
}

export async function apiAdminListPlans(): Promise<SubscriptionPlan[] | ApiErrorResponse> {
  const res = await fetch(`${API_BASE}/admin/plans`, {
    headers: authHeaders(),
  });

  const data = await parseApiResponse(res);

  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message || 'Unable to load subscription plans right now.',
    };
  }

  return extractPlans(data);
}

export async function apiAdminCreatePlan(plan: AdminPlanPayload) {
  const res = await fetch(`${API_BASE}/admin/plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(plan),
  });

  const data = await parseApiResponse(res);

  return {
    ...data,
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    status: res.status,
    message: data?.message || (res.ok ? 'Plan created successfully.' : 'Unable to create the plan right now.'),
  };
}

export async function apiAdminUpdatePlan(id: number, plan: AdminPlanPayload) {
  const res = await fetch(`${API_BASE}/admin/plans/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(plan),
  });

  const data = await parseApiResponse(res);

  return {
    ...data,
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    status: res.status,
    message: data?.message || (res.ok ? 'Plan updated successfully.' : 'Unable to update the plan right now.'),
  };
}

export async function apiAdminUpdatePlanStatus(id: number, isActive: boolean) {
  const res = await fetch(`${API_BASE}/admin/plans/${id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ is_active: isActive }),
  });

  const data = await parseApiResponse(res);

  return {
    ...data,
    success: typeof data?.success === 'boolean' ? data.success : res.ok,
    status: res.status,
    message: data?.message || (res.ok ? 'Plan status updated successfully.' : 'Unable to update the plan status right now.'),
  };
}

export async function apiFetchArticlePdfBlobUrl(id: number): Promise<string> {
  const res = await fetch(`${API_BASE}/articles/${id}/pdf`, {
    headers: authHeaders(),
  });

  if (!res.ok) {
    throw new Error('Unable to load PDF preview.');
  }

  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function apiAdminCreatePdfArticle(article: {
  title: string;
  slug: string;
  excerpt: string;
  pdf_filename: string;
  published: boolean;
}) {
  return apiAdminCreateArticle({
    ...article,
    content_type: 'pdf',
  });
}

export function getPdfUrl(id: number) {
  return `${API_BASE}/articles/${id}/pdf`;
}
